import {
  ReactElement,
  ReactNode,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { useHasFieldPermissions } from '../../../../api/authorization.provider';
import { useScratchpadServiceContext } from '../../../../components/machine/scratchpad/scratchpad-service-context-provider';
import { CrudPermissionAction } from '../../../../components/role/role.types';
import { FlowItemParameterRef } from '../model/flow.types';
import { useFlowServices } from './flow-service.hooks';

export interface FlowPermissionsContextValue {
  parameterPermissions: Record<FlowItemParameterRef, boolean>;
}

const FlowPermissionsContext = createContext<FlowPermissionsContextValue>({
  parameterPermissions: {},
});

interface FlowPermissionsContextProviderProps {
  children: ReactNode;
}

const FlowPermissionsContextProvider = (
  props: FlowPermissionsContextProviderProps
): ReactElement => {
  const { children } = props;
  const { scratchpad } = useScratchpadServiceContext();
  const { flowService } = useFlowServices();

  const parameterPermissions = useHasFieldPermissions({
    action: CrudPermissionAction.UPDATE,
    sub: scratchpad.app.slug,
    object: {
      ...scratchpad,
      equipment: flowService.getEquipmentName(),
    },
    fields: flowService.getAvailableFlowItemParameterRefs(),
  });

  const flowPermissionsContextValue: FlowPermissionsContextValue =
    useMemo(() => {
      return {
        parameterPermissions,
      };
    }, [parameterPermissions]);

  return (
    <FlowPermissionsContext.Provider value={flowPermissionsContextValue}>
      {children}
    </FlowPermissionsContext.Provider>
  );
};

function useFlowPermissionsContext(): FlowPermissionsContextValue {
  return useContext(FlowPermissionsContext);
}

export { FlowPermissionsContextProvider, useFlowPermissionsContext };
